import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseComponent, DropdownComponent } from '@sircon/sircon-ui-framework';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAnglesLeft, faAnglesRight } from '@fortawesome/pro-regular-svg-icons';

import { NavigationService, NavigationTab } from 'src/services/navigation.service';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { UserContextService } from 'src/services/user-context.service';
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UserContext } from 'src/app/core/models/user/user-context.model';
import { DefaultConfigurableRole } from 'src/app/core/models/authorization/role.model';
import { AuthorizeDirective } from 'src/framework/authorize/authorize.directive';

/**
 * Controller for the side rail component in the application
 * Note: Authorization is now handled at app.component level via conditional rendering
 */
@Component({
    selector: 'app-side-rail',
    templateUrl: './side-rail.component.html',
    styleUrls: ['./side-rail.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [RouterLink, NgClass, NgFor, NgIf, NgbPopover, NgTemplateOutlet,
        DropdownComponent, FaIconComponent, AuthorizeDirective]
})
export class SideRailComponent extends BaseComponent implements OnInit {
    navigationOptions: NavigationTab[];
    isExpanded = true;
    closeDelayTime = 1500;  // second-and-a-half

    faAnglesLeft: IconDefinition = faAnglesLeft;
    faAnglesRight: IconDefinition = faAnglesRight;

    userContext: UserContext;

    constructor(private route: ActivatedRoute,
                private router: Router,
                private navigationService: NavigationService,
                private userContextService: UserContextService) {
        super();
    }

    ngOnInit(): void {
        this.navigationOptions = this.navigationService.getNavigationOptions();

        this.userContextService.context$.subscribe(() => {
            this.navigationOptions = this.navigationService.getNavigationOptions();
        });

        this.userContext = this.userContextService.userContext;
    }

    /**
     * Determine if a nav item in the siderail should be highlighted as active based on pattern matching of the current route.
     * @param navItem
     */
    isActive(navItem: NavigationTab): boolean {
        const routePath = this.route.snapshot.routeConfig?.path || this.router.url;

        if (routePath?.includes('framedTransaction')) {
            return this.handleFramedTransactionPages(navItem);
        }

        return this.doesRouteMatchNavItem(routePath, navItem);
    }

    doesRouteMatchNavItem(routeName: string | undefined, navItem: NavigationTab): boolean {
        if (!routeName) {
            return false;
        }

        // Normalize route name by removing leading '/' and any hash
        const normalizedRoute = routeName.replace(/^#?\//g, '');

        // Convert nav item route to normalized slash notation for comparison
        const navItemPath = this.normalizeRouteForComparison(navItem.route);
        const firstSegment = navItemPath.split('/')[0];

        // Check if current route matches the nav item or any of its children
        return normalizedRoute.startsWith(firstSegment) || !!navItem.children?.find((childTab) => {
            const childPath = this.normalizeRouteForComparison(childTab.route);
            return normalizedRoute.startsWith(childPath);
        });
    }

    /**
     * Normalize a route array to slash notation for comparison
     * Example: ['/dashboard', 'activity'] -> 'dashboard/activity'
     */
    private normalizeRouteForComparison(route: string[]): string {
        // Array format: join and remove leading slash
        return route.join('/').replace(/^\//, '');
    }

    /**
     * Framed transaction pages have 2 routes - dashboard.framedTransaction and network.entity.framedTransaction. Since the current route
     * doesn't give us enough context of which page the user used to navigate to it, we have to look at the previously visited route
     * so we can highlight the siderail more appropriately.
     *
     * TODO: Implement navigation history tracking for Angular Router to properly highlight side-rail
     * @param navItem
     */
    handleFramedTransactionPages(navItem: NavigationTab): boolean {
        // Fallback: check if the route path contains dashboard or network to determine context
        const routePath = this.route.snapshot.url.join('/');
        const routeName = routePath.includes('network') ? 'network' : 'dashboard';
        return this.doesRouteMatchNavItem(routeName, navItem);
    }

    toggleSideRailExpand(): void {
        this.isExpanded = !this.isExpanded;
    }

    isCarrierUserRecruiter(): boolean {
        const isRecruiter = this.userContext?.user?.configurableRoleCodes?.includes(DefaultConfigurableRole.Recruiter);
        return this.userContextService.isCarrierAccount() && isRecruiter;
    }

    /**
     * Convert route array to Angular Router path
     * Example: ['/dashboard', 'activity'] -> '/dashboard/activity'
     */
    convertRouteToPath(route: string[]): string {
        if (!route || route.length === 0) return '/';
        // Join array segments into path
        return route.join('/').replace(/\/\//g, '/'); // Remove any double slashes
    }
}
